'use client';
import React, { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    tourType: '',
    dates: '',
    groupSize: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
    }, 1500);
  };

  return (
    <section id="contact" className="section-padding bg-navy">
      <div className="container-luxury">
        {/* Header */}
        <div className="text-center mb-16 animate-on-scroll">
          <span className="tag-gold">Contact Us</span>
          <h2 className="section-title mt-4 mb-4 text-white">
            Start Planning Your
            <br />
            <span className="text-gold italic">Dream Morocco Journey</span>
          </h2>
          <div className="gold-divider" />
          <p className="section-subtitle mx-auto mt-4 text-white/70">
            Contact David directly to discuss your perfect Morocco experience.
            He responds personally to every inquiry within 2 hours.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Contact Info */}
          <div className="lg:col-span-2 animate-on-scroll-left">
            <h3 className="font-serif text-2xl font-bold text-white mb-6">
              Get in Touch
            </h3>

            {/* Contact Cards */}
            <div className="space-y-4 mb-8">
              <a
                href="https://wa.me/212668702424"
                target="_blank"
                rel="noopener noreferrer"
                className="contact-info-card"
              >
                <div className="w-12 h-12 bg-green-600 flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                </div>
                <div>
                  <div className="font-sans text-xs text-white/60 uppercase tracking-wider">WhatsApp (Preferred)</div>
                  <div className="font-sans font-bold text-white group-hover:text-gold transition-colors">+212 668 702 424</div>
                  <div className="font-sans text-xs text-green-400">Typically responds within 2 hours</div>
                </div>
              </a>

              <a
                href="tel:+212668702424"
                className="contact-info-card"
              >
                <div className="w-12 h-12 bg-gold flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-navy-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <div>
                  <div className="font-sans text-xs text-white/60 uppercase tracking-wider">Phone</div>
                  <div className="font-sans font-bold text-white group-hover:text-gold transition-colors">+212 668 702 424</div>
                </div>
              </a>

              <a
                href="mailto:dbdahantours@gmail.com"
                className="contact-info-card"
              >
                <div className="w-12 h-12 bg-gold flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-navy-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <div className="font-sans text-xs text-white/60 uppercase tracking-wider">Email</div>
                  <div className="font-sans font-bold text-white group-hover:text-gold transition-colors">dbdahantours@gmail.com</div>
                </div>
              </a>

              <div className="contact-info-card">
                <div className="w-12 h-12 bg-gold flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-navy-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <div className="font-sans text-xs text-white/60 uppercase tracking-wider">Location</div>
                  <div className="font-sans font-bold text-white">Casablanca, Morocco</div>
                  <div className="font-sans text-xs text-white/50">Serving all of Morocco</div>
                </div>
              </div>
            </div>

            {/* Response Time */}
            <div className="bg-gold/10 border border-gold/30 p-4 mb-8">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                <span className="font-sans text-sm text-white/90">
                  <span className="font-bold text-gold">David typically responds within 2 hours</span>
                  {' '}via WhatsApp
                </span>
              </div>
            </div>

            {/* Languages */}
            <div className="mb-8">
              <h4 className="font-sans text-xs text-white/60 uppercase tracking-wider mb-3">Languages Spoken</h4>
              <div className="flex flex-wrap gap-2">
                {['🇺🇸 English', '🇫🇷 French', '🇲🇦 Arabic', '🇮🇱 Hebrew'].map((lang) => (
                  <span key={lang} className="font-sans text-xs font-semibold text-white bg-white/10 px-3 py-1.5 border border-white/20">
                    {lang}
                  </span>
                ))}
              </div>
            </div>

            {/* Map */}
            <div className="relative h-48 bg-white/5 border border-white/10 overflow-hidden">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d106376.64!2d-7.6899!3d33.5731!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xda7cd4778aa113b%3A0xb06c1d84f310fd3!2sCasablanca%2C%20Morocco!5e0!3m2!1sen!2sus!4v1700000000000"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="MoroccoJewishTours location in Casablanca, Morocco"
              />
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-3 animate-on-scroll-right">
            <div className="bg-white p-8 md:p-10">
              {submitted ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gold/10 border-2 border-gold flex items-center justify-center mx-auto mb-6">
                    <svg className="w-8 h-8 text-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h3 className="font-serif text-2xl font-bold text-navy mb-3">
                    Thank You!
                  </h3>
                  <p className="font-sans text-gray-600 mb-2">
                    Your message has been sent successfully.
                  </p>
                  <p className="font-sans text-gray-500 text-sm mb-6">
                    David will respond personally within 2 hours. For immediate assistance, contact him on WhatsApp.
                  </p>
                  <a
                    href="https://wa.me/212668702424"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-whatsapp"
                  >
                    Also Message on WhatsApp
                  </a>
                </div>
              ) : (
                <>
                  <h3 className="font-serif text-2xl font-bold text-navy mb-2">
                    Request Your Custom Itinerary
                  </h3>
                  <p className="font-sans text-sm text-gray-500 mb-8">
                    Fill in the form below and David will create a personalized proposal for you.
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block font-sans text-xs font-bold text-navy uppercase tracking-wider mb-2">
                          Full Name *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="Your full name"
                          className="input-luxury"
                        />
                      </div>
                      <div>
                        <label className="block font-sans text-xs font-bold text-navy uppercase tracking-wider mb-2">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="your@email.com"
                          className="input-luxury"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block font-sans text-xs font-bold text-navy uppercase tracking-wider mb-2">
                          Phone / WhatsApp
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+1 234 567 8900"
                          className="input-luxury"
                        />
                      </div>
                      <div>
                        <label className="block font-sans text-xs font-bold text-navy uppercase tracking-wider mb-2">
                          Country
                        </label>
                        <select
                          name="country"
                          value={formData.country}
                          onChange={handleChange}
                          className="input-luxury"
                        >
                          <option value="">Select your country</option>
                          <option>United States</option>
                          <option>Canada</option>
                          <option>United Kingdom</option>
                          <option>Israel</option>
                          <option>France</option>
                          <option>Australia</option>
                          <option>Germany</option>
                          <option>Netherlands</option>
                          <option>Belgium</option>
                          <option>Switzerland</option>
                          <option>Other</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block font-sans text-xs font-bold text-navy uppercase tracking-wider mb-2">
                          Tour Type *
                        </label>
                        <select
                          name="tourType"
                          value={formData.tourType}
                          onChange={handleChange}
                          required
                          className="input-luxury"
                        >
                          <option value="">Select tour type</option>
                          <option>Private Morocco Tour</option>
                          <option>Jewish Heritage Tour</option>
                          <option>Genealogy Tour</option>
                          <option>Casablanca Tour</option>
                          <option>Day Trip</option>
                          <option>Airport Transfer</option>
                          <option>Cruise Shore Excursion</option>
                          <option>Custom Itinerary</option>
                          <option>Sahara Desert Tour</option>
                          <option>VIP Travel</option>
                          <option>Multi-City Tour</option>
                        </select>
                      </div>
                      <div>
                        <label className="block font-sans text-xs font-bold text-navy uppercase tracking-wider mb-2">
                          Travel Dates
                        </label>
                        <input
                          type="text"
                          name="dates"
                          value={formData.dates}
                          onChange={handleChange}
                          placeholder="e.g. March 15-22, 2025"
                          className="input-luxury"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block font-sans text-xs font-bold text-navy uppercase tracking-wider mb-2">
                        Group Size
                      </label>
                      <select
                        name="groupSize"
                        value={formData.groupSize}
                        onChange={handleChange}
                        className="input-luxury"
                      >
                        <option value="">Select group size</option>
                        <option>1 person</option>
                        <option>2 people</option>
                        <option>3-4 people</option>
                        <option>5-8 people</option>
                        <option>9+ people</option>
                      </select>
                    </div>

                    <div>
                      <label className="block font-sans text-xs font-bold text-navy uppercase tracking-wider mb-2">
                        Your Message *
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        placeholder="Tell David about your dream Morocco experience, specific interests, special requirements (kosher meals, accessibility, genealogy research, etc.)..."
                        className="textarea-luxury"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="btn-gold w-full justify-center"
                    >
                      {loading ? (
                        <>
                          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Sending...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                          </svg>
                          Send My Request
                        </>
                      )}
                    </button>

                    <p className="font-sans text-xs text-gray-400 text-center">
                      Or contact David directly on{' '}
                      <a
                        href="https://wa.me/212668702424"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-600 font-bold hover:underline"
                      >
                        WhatsApp
                      </a>
                      {' '}for an instant response.
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
